import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import PieceViewer from '../components/PieceViewer';
import ScoreInChat from '../components/ScoreInChat';
import './PracticePage.css';
import remarkGfm from 'remark-gfm';


const PracticePage = ({ setSelectedPiece, user, currentChatId, onChatSelect, onNewChat }) => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [sidebarPiece, setSidebarPiece] = useState(null);
  const [sidebarWidth, setSidebarWidth] = useState(700);
  const [measureContext, setMeasureContext] = useState(null); // Track measure and part context
  const isResizingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Load conversation history when chat changes
  useEffect(() => {
    const loadConversationHistory = async () => {
      if (user && user.id && currentChatId) {
        try {
          const response = await axios.get(`${API_BASE}/groq/conversation`, { 
            params: { user_id: user.id, chat_id: currentChatId } 
          });
          if (response.data && response.data.length > 0) {
            setMessages(response.data);
          } else {
            setMessages([]);
          }
        } catch (error) {
          console.error('Error loading conversation history:', error);
          setMessages([]);
        }
      } else {
        setMessages([]);
      }
      // Clear input and measure prefix when chat changes
      setInput('');
      setMeasureContext(null);
    };
    
    loadConversationHistory();
  }, [user, currentChatId]);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!isResizingRef.current) return;
      const dx = startXRef.current - e.clientX;
      const newWidth = Math.max(240, Math.min(window.innerWidth * 2, startWidthRef.current + dx));
      setSidebarWidth(newWidth);
    };

    const onMouseUp = () => {
      if (isResizingRef.current) {
        isResizingRef.current = false;
        document.body.style.cursor = '';
      }
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const handleMouseDown = (e) => {
    isResizingRef.current = true;
    startXRef.current = e.clientX;
    startWidthRef.current = sidebarWidth;
    document.body.style.cursor = 'ew-resize';
    e.preventDefault();
  };

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    isResizingRef.current = true;
    startXRef.current = touch.clientX;
    startWidthRef.current = sidebarWidth;
    const onTouchMove = (ev) => {
      if (!isResizingRef.current) return;
      const t = ev.touches[0];
      const dx = startXRef.current - t.clientX;
      const newWidth = Math.max(240, Math.min(window.innerWidth - 200, startWidthRef.current + dx));
      setSidebarWidth(newWidth);
    };
    const onTouchEnd = () => {
      isResizingRef.current = false;
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd);
  };

  const API_BASE = process.env.REACT_APP_API_BASE || (window.location.hostname === 'localhost' ? 'http://localhost:5000' : 'https://studyvarious.com/api');

  const sendMessage = async () => {
    if (!input.trim() || !currentChatId) return;
    
    let prompt = input;
    // Include measure context if it exists
    if (measureContext) {
      prompt = `**User asks about Measure ${measureContext.measure} in Part ${measureContext.part} of Piece ${measureContext.piece}** ${input}`;
    }
    
    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setMeasureContext(null); // Clear the context after sending
    setLoading(true);

    try {
      const requestData = { 
        prompt: prompt,
        chat_id: currentChatId
      };
      if (user && user.id) {
        requestData.user_id = user.id;
      }
      const response = await axios.post(`${API_BASE}/groq/query`, requestData);
      const aiMessage = { role: 'assistant', content: response.data.response, svgs: response.data.svgs, hash: response.data.hash, id: response.data.id };
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      const errorMessage = { role: 'assistant', content: 'Sorry, there was an error processing your request.' };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleAskStudyvarious = (measureNumber, partId, pieceId) => {
    setMeasureContext({ measure: measureNumber, part: partId, piece: pieceId });
  };

  const removeMeasureContext = () => {
    setMeasureContext(null);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      sendMessage();
    }
  };

  return (
    <div className={`practice-page ${showSidebar ? 'with-sidebar' : ''}`}>
      <div className="practice-main">
        <section className={`chat-main ${messages.length === 0 ? 'with-header' : 'without-header'}`}>
          {messages.length === 0 && (
            <div className="results-header">
              <div>
                <h2 className="results-title">Practice Chat</h2>
                <p className="results-subtitle">Chat with AI to help you practice your pieces.</p>
              </div>
            </div>
          )}

          <div className="chat-container">
            <div className="chat-messages">
              {messages.map((msg, index) => (
                <div key={index} className={`chat-message ${msg.role}`}>
                  {msg.role === 'user' ? (
                    <>
                      <strong>You:</strong> {msg.content.startsWith("**User asks") ? msg.content.replace(/^.*?\*\* /, "") : msg.content}
                    </>
                  ) : (
                    <div className="ai-message">
                    <div className="ai-icon" style={{ backgroundImage: `url(/studyvarious_avatar.png)` }}></div>
                      <div className="ai-content">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {msg.processed_content || msg.content}
                        </ReactMarkdown>
                        <ScoreInChat
                          svgs={msg.svgs}
                          hash={msg.hash}
                          id={msg.id}
                          onOpenInPlayer={(id) => {
                            setSidebarPiece({ id });
                            setShowSidebar(true);
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {loading && (
                <div className="chat-message assistant">
                  <div className="ai-message loading">
                    <div className="ai-icon" style={{ backgroundImage: `url(/studyvarious_avatar.png)` }}></div>
                    <div className="ai-content">
                      <div className="typing-indicator">
                        <span></span><span></span><span></span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            <div className={`chat-input-container ${measureContext ? 'has-measure-prefix' : ''}`}>
              {measureContext && (
                <div className="measure-prefix">
                  <span className="enter-symbol">↳</span>
                  <span className="measure-text">"Measure {measureContext.measure}"</span>
                  <button 
                    onClick={removeMeasureContext}
                    className="remove-prefix-btn"
                    aria-label="Remove measure reference"
                    title="Remove measure reference"
                  >
                    ×
                  </button>
                </div>
              )}
              <div className="input-row">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={currentChatId ? "Ask about music practice..." : "Select a chat to start messaging..."}
                  className="chat-input"
                  disabled={!currentChatId}
                />
                <button
                  onClick={sendMessage}
                  disabled={loading || !currentChatId || !input.trim()}
                  className={`chat-send-button ${loading ? 'loading' : ''}`}
                  aria-label="Send message"
                  title="Send"
                >
                  {/* paper-plane icon */}
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M2 21L23 12 2 3v7l15 2-15 2v7z" fill="currentColor" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
      {showSidebar && (
        <div className="practice-sidebar" style={{ width: `${sidebarWidth}px` }}>
          <div
            className="sidebar-resizer"
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize sidebar"
          />
          <div className="sidebar-content">
            <button onClick={() => { setSelectedPiece(sidebarPiece); navigate('/piece'); }} className="expand-sidebar-button" title="Expand to full page">⇱</button>
            <button onClick={() => setShowSidebar(false)} className="close-sidebar-button" title="Close sidebar">×</button>
            <PieceViewer
              selectedPiece={sidebarPiece}
              addFavorite={() => {}} // No-op for sidebar
              removeFavorite={() => {}} // No-op for sidebar
              isFavorited={false} // Not favorited in sidebar
              inlineAudioPlayer={true}
              onAskStudyvarious={handleAskStudyvarious}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PracticePage;
